import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { ThreeCanvas } from "@remotion/three";
import * as THREE from "three";

// --- PROCEDURAL 3D ZUI PET CHARACTER ---
const Zui3DModel: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  // Idle bounce and breathing
  const breathe = Math.sin(frame * 0.1) * 0.035;
  const bobY = Math.sin(frame * 0.12) * 0.07;
  const headTilt = Math.sin(frame * 0.08) * 0.05;
  const earTwitchLeft = Math.sin(frame * 0.18) * 0.08;
  const earTwitchRight = Math.cos(frame * 0.14) * 0.06;
  const tailWag = Math.sin(frame * 0.22) * 0.5;

  // Waving right arm (excited hello wave)
  const wave = Math.sin(frame * 0.35) * 0.45;

  // Blinking logic (blinks every ~80 frames)
  const blinkCycle = frame % 90;
  const isBlinking = blinkCycle > 82 && blinkCycle < 88;
  const eyeScaleY = isBlinking ? 0.08 : 1.0;

  // Materials
  const furMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFBF5", // Ultra soft warm marshmallow
    roughness: 0.42,
    metalness: 0.02,
  });

  const bellyMaterial = new THREE.MeshStandardMaterial({
    color: "#FFEAD8", // Soft peach belly
    roughness: 0.5,
    metalness: 0.0,
  });

  const innerEarMaterial = new THREE.MeshStandardMaterial({
    color: "#FFA8BA", // Blossom pink
    roughness: 0.38,
    metalness: 0.0,
  });

  const eyeGlossMaterial = new THREE.MeshStandardMaterial({
    color: "#0D0A18", // Glossy obsidian anime eye
    roughness: 0.05,
    metalness: 0.18,
  });

  const eyeHighlightMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFFFF",
    roughness: 0.1,
    metalness: 0.0,
  });

  const blushMaterial = new THREE.MeshStandardMaterial({
    color: "#FF5E7E",
    emissive: new THREE.Color("#FF3366"),
    emissiveIntensity: 0.4,
    roughness: 0.45,
  });

  const noseMaterial = new THREE.MeshStandardMaterial({
    color: "#FF6B8B",
    roughness: 0.25,
    metalness: 0.05,
  });

  const collarMaterial = new THREE.MeshStandardMaterial({
    color: "#00E5FF", // Electric cyan collar
    emissive: new THREE.Color("#0088AA"),
    emissiveIntensity: 0.3,
    roughness: 0.2,
    metalness: 0.35,
  });

  const bellGoldMaterial = new THREE.MeshStandardMaterial({
    color: "#FFD700", // Polished gold bell
    roughness: 0.12,
    metalness: 0.9,
  });

  const podiumMaterial = new THREE.MeshStandardMaterial({
    color: "#18181B",
    roughness: 0.35,
    metalness: 0.2,
  });

  const podiumRingMaterial = new THREE.MeshStandardMaterial({
    color: "#00E5FF",
    emissive: new THREE.Color("#00E5FF"),
    emissiveIntensity: 0.9,
    roughness: 0.2,
  });

  return (
    // Raised vertically so full body and podium sit comfortably above the bottom HUD
    <group position={[0, 0.25 + bobY, 0]}>
      {/* --- PODIUM / STAGE --- */}
      <group position={[0, -1.28 - bobY, 0]}>
        <mesh material={podiumMaterial} receiveShadow position={[0, -0.15, 0]}>
          <cylinderGeometry args={[2.4, 2.6, 0.3, 48]} />
        </mesh>
        {/* Glowing Cyan Accent Ring */}
        <mesh material={podiumRingMaterial} position={[0, 0.01, 0]}>
          <ringGeometry args={[2.25, 2.4, 48]} />
        </mesh>
      </group>

      {/* --- MAIN BODY --- */}
      <group scale={[1 + breathe, 1 - breathe * 0.5, 1 + breathe]}>
        {/* Chubby Torso */}
        <mesh material={furMaterial} castShadow position={[0, -0.22, 0]} scale={[1.08, 0.98, 1.05]}>
          <sphereGeometry args={[1.0, 36, 36]} />
        </mesh>

        {/* Soft Peach Belly Patch */}
        <mesh material={bellyMaterial} position={[0, -0.25, 0.8]} scale={[0.82, 0.88, 0.32]}>
          <sphereGeometry args={[0.85, 24, 24]} />
        </mesh>
      </group>

      {/* --- COLLAR & GOLD BELL --- */}
      <group position={[0, 0.42, 0]} rotation={[0.18, 0, 0]}>
        {/* Explicit Visible Cyan Collar Band */}
        <mesh material={collarMaterial} position={[0, 0, 0]}>
          <torusGeometry args={[0.86, 0.1, 16, 36]} />
        </mesh>
        {/* Golden Bell Charm hanging down */}
        <mesh material={bellGoldMaterial} position={[0, -0.2, 0.92]}>
          <sphereGeometry args={[0.22, 24, 24]} />
        </mesh>
      </group>

      {/* --- HEAD & EXPRESSIVE FACE --- */}
      <group position={[0, 1.18, 0]} rotation={[0, 0, headTilt]}>
        {/* Cute Chubby Head */}
        <mesh material={furMaterial} castShadow scale={[1.18, 1.02, 1.1]}>
          <sphereGeometry args={[1.0, 36, 36]} />
        </mesh>

        {/* --- EARS (CAT/FOX CHIBI HYBRID) --- */}
        {/* Left Ear */}
        <group position={[-0.72, 0.95, -0.05]} rotation={[0.05, 0.1, 0.38 + earTwitchLeft]}>
          <mesh material={furMaterial} scale={[0.5, 0.9, 0.38]}>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          {/* Inner Ear Blossom Pink */}
          <mesh material={innerEarMaterial} position={[0, -0.04, 0.12]} scale={[0.34, 0.68, 0.18]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>

        {/* Right Ear */}
        <group position={[0.72, 0.95, -0.05]} rotation={[0.05, -0.1, -0.38 - earTwitchRight]}>
          <mesh material={furMaterial} scale={[0.5, 0.9, 0.38]}>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          {/* Inner Ear Blossom Pink */}
          <mesh material={innerEarMaterial} position={[0, -0.04, 0.12]} scale={[0.34, 0.68, 0.18]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>

        {/* --- EYES (BIG GLOSSY ANIME PET EYES) --- */}
        {/* Left Eye */}
        <group position={[-0.42, 0.05, 1.02]} scale={[1, eyeScaleY, 1]}>
          <mesh material={eyeGlossMaterial} scale={[0.22, 0.26, 0.14]}>
            <sphereGeometry args={[1, 24, 24]} />
          </mesh>
          {/* Sparkle 1 (Main Big Light) */}
          <mesh material={eyeHighlightMaterial} position={[0.05, 0.08, 0.12]} scale={[0.08, 0.08, 0.04]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          {/* Sparkle 2 (Mini Secondary Light) */}
          <mesh material={eyeHighlightMaterial} position={[-0.07, -0.07, 0.12]} scale={[0.04, 0.04, 0.03]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>

        {/* Right Eye */}
        <group position={[0.42, 0.05, 1.02]} scale={[1, eyeScaleY, 1]}>
          <mesh material={eyeGlossMaterial} scale={[0.22, 0.26, 0.14]}>
            <sphereGeometry args={[1, 24, 24]} />
          </mesh>
          {/* Sparkle 1 (Main Big Light) */}
          <mesh material={eyeHighlightMaterial} position={[0.05, 0.08, 0.12]} scale={[0.08, 0.08, 0.04]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          {/* Sparkle 2 (Mini Secondary Light) */}
          <mesh material={eyeHighlightMaterial} position={[-0.07, -0.07, 0.12]} scale={[0.04, 0.04, 0.03]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>

        {/* --- CUTE CHEEKS (BLUSH) --- */}
        <mesh material={blushMaterial} position={[-0.66, -0.15, 0.9]} scale={[0.22, 0.11, 0.05]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={blushMaterial} position={[0.66, -0.15, 0.9]} scale={[0.22, 0.11, 0.05]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>

        {/* --- BUTTON NOSE --- */}
        <mesh material={noseMaterial} position={[0, -0.02, 1.12]} scale={[0.08, 0.055, 0.06]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>

        {/* --- CUTE MOUTH (CAT SMILE CURVE) --- */}
        <group position={[0, -0.14, 1.08]} rotation={[0, 0, Math.PI]}>
          <mesh material={noseMaterial} position={[-0.07, 0, 0]}>
            <torusGeometry args={[0.07, 0.02, 12, 18, Math.PI * 0.9]} />
          </mesh>
          <mesh material={noseMaterial} position={[0.07, 0, 0]}>
            <torusGeometry args={[0.07, 0.02, 12, 18, Math.PI * 0.9]} />
          </mesh>
        </group>
      </group>

      {/* --- ARMS / PAWS --- */}
      {/* Left Arm (Resting cute on side/belly) */}
      <group position={[-0.92, 0.08, 0.25]} rotation={[0.4, 0, 0.2]}>
        <mesh material={furMaterial} scale={[0.26, 0.42, 0.26]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        {/* Pink Paw Tip */}
        <mesh material={innerEarMaterial} position={[0, -0.3, 0.06]} scale={[0.15, 0.15, 0.09]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Right Arm (WAVING HELLO!) */}
      <group position={[0.95, 0.25, 0.25]} rotation={[-0.2, 0.2, -0.8 + wave]}>
        <mesh material={furMaterial} scale={[0.26, 0.45, 0.26]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        {/* Pink Paw Tip */}
        <mesh material={innerEarMaterial} position={[0, 0.34, 0.04]} scale={[0.16, 0.16, 0.09]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* --- FEET / HIND PAWS (SITTING CHIBI POSE) --- */}
      {/* Left Foot */}
      <group position={[-0.52, -1.02, 0.58]} rotation={[-0.2, 0.2, 0]}>
        <mesh material={furMaterial} scale={[0.34, 0.2, 0.45]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        {/* Sole Pink Pad */}
        <mesh material={innerEarMaterial} position={[0, 0.07, 0.13]} scale={[0.16, 0.05, 0.18]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Right Foot */}
      <group position={[0.52, -1.02, 0.58]} rotation={[-0.2, -0.2, 0]}>
        <mesh material={furMaterial} scale={[0.34, 0.2, 0.45]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        {/* Sole Pink Pad */}
        <mesh material={innerEarMaterial} position={[0, 0.07, 0.13]} scale={[0.16, 0.05, 0.18]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* --- FLUFFY WAGGING TAIL --- */}
      <group position={[0, -0.45, -0.9]} rotation={[-0.35, tailWag, 0]}>
        <mesh material={furMaterial} scale={[0.22, 0.32, 0.7]} position={[0, 0.22, -0.22]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        {/* Tail White Puff Tip */}
        <mesh material={bellyMaterial} position={[0, 0.46, -0.6]} scale={[0.2, 0.2, 0.2]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* --- FLOATING SPARKLE STARS AROUND ZUI --- */}
      <group position={[1.5, 1.6 + Math.sin(frame * 0.12) * 0.1, 0.2]}>
        <mesh material={podiumRingMaterial} rotation={[frame * 0.05, frame * 0.05, 0]}>
          <octahedronGeometry args={[0.15, 0]} />
        </mesh>
      </group>
      <group position={[-1.4, 1.2 + Math.cos(frame * 0.1) * 0.1, 0.4]}>
        <mesh material={bellGoldMaterial} rotation={[frame * 0.03, 0, frame * 0.04]}>
          <octahedronGeometry args={[0.12, 0]} />
        </mesh>
      </group>
    </group>
  );
};

// --- REMOTION COMPOSITION: ZUI PET INTRO ---
export const ZuiPetIntro: React.FC = () => {
  const { width, height, fps } = useVideoConfig();
  const frame = useCurrentFrame();

  // Entrance spring animations
  const introPop = spring({
    frame,
    fps,
    config: { damping: 14, stiffness: 100, mass: 0.8 },
  });

  const cardSlide = spring({
    frame: frame - 10,
    fps,
    config: { damping: 16, stiffness: 120 },
  });

  // Camera subtle rotation / turntable
  const camRotY = Math.sin(frame * 0.03) * 0.18;
  const camZ = interpolate(frame, [0, 90], [8.0, 7.8], {
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#09090B", // Deep slate black
        overflow: "hidden",
        fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
      }}
    >
      {/* BACKGROUND GRADIENT & RADIAL GLOW */}
      <div
        style={{
          position: "absolute",
          top: "22%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 900,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0, 229, 255, 0.18) 0%, rgba(255, 107, 139, 0.12) 45%, transparent 70%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />

      {/* --- 3D THREE.JS CANVAS --- */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          zIndex: 10,
          transform: `scale(${introPop})`,
          transformOrigin: "center center",
        }}
      >
        <ThreeCanvas
          width={width}
          height={height}
          camera={{
            // Camera position framed so full body from ears to podium is clear
            position: [Math.sin(camRotY) * camZ, 0.45, Math.cos(camRotY) * camZ],
            fov: 38,
          }}
        >
          {/* Studio Lights */}
          <ambientLight intensity={1.8} />
          {/* Main Key Light */}
          <directionalLight position={[5, 8, 6]} intensity={3.2} color="#FFFFFF" />
          {/* Warm Side Light */}
          <directionalLight position={[-6, 4, 3]} intensity={2.4} color="#FFE4D6" />
          {/* Cyan Rim / Back Light to pop silhouette */}
          <directionalLight position={[0, 6, -5]} intensity={4.5} color="#00E5FF" />
          {/* Under Fill Light */}
          <pointLight position={[0, -2, 3]} intensity={1.6} color="#FF99B8" />

          {/* Real Procedural 3D Model */}
          <Zui3DModel frame={frame} fps={fps} />
        </ThreeCanvas>
      </div>

      {/* --- 2D OVERLAYS & HUD (STRICT TIKTOK SAFE ZONE: Y 220px - 1440px) --- */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 50,
          pointerEvents: "none",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 210, // Strict top clearance
          paddingBottom: 420, // Strict bottom clearance
          boxSizing: "border-box",
        }}
      >
        {/* TOP HEADER SECTION */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            opacity: introPop,
            transform: `translateY(${(1 - introPop) * -30}px)`,
          }}
        >
          {/* Top Pill Badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "rgba(24, 24, 27, 0.85)",
              border: "1.5px solid rgba(0, 229, 255, 0.4)",
              borderRadius: 999,
              padding: "8px 22px",
              boxShadow: "0 8px 24px rgba(0, 229, 255, 0.2)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#00E5FF",
                boxShadow: "0 0 10px #00E5FF",
              }}
            />
            <span
              style={{
                color: "#F4F4F5",
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              ORIGINAL 3D CHARACTER • FROM SCRATCH
            </span>
          </div>

          {/* Big Name Display */}
          <h1
            style={{
              margin: 0,
              fontSize: 78,
              fontWeight: 900,
              letterSpacing: "-0.03em",
              color: "#FFFFFF",
              textShadow: "0 8px 32px rgba(0,0,0,0.8)",
              textAlign: "center",
              lineHeight: 1,
            }}
          >
            MEET <span style={{ color: "#00E5FF" }}>ZUI</span>
          </h1>

          <div
            style={{
              color: "#A1A1AA",
              fontSize: 20,
              fontWeight: 600,
              letterSpacing: "0.04em",
            }}
          >
            Your New 3D Companion & Mascot
          </div>
        </div>

        {/* BOTTOM FLOATING INFO CARD */}
        <div
          style={{
            width: 860,
            background: "rgba(18, 18, 22, 0.92)",
            border: "2px solid rgba(255, 255, 255, 0.12)",
            borderRadius: 28,
            padding: "22px 30px",
            boxShadow: "0 24px 60px rgba(0, 0, 0, 0.7), 0 0 40px rgba(0, 229, 255, 0.1)",
            backdropFilter: "blur(20px)",
            display: "flex",
            flexDirection: "column",
            gap: 14,
            transform: `translateY(${(1 - cardSlide) * 40}px)`,
            opacity: cardSlide,
          }}
        >
          {/* Card Top Row: Tags */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <span
                style={{
                  background: "rgba(0, 229, 255, 0.15)",
                  color: "#00E5FF",
                  border: "1px solid rgba(0, 229, 255, 0.35)",
                  padding: "6px 14px",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: "0.05em",
                }}
              >
                100% THREE.JS PROCEDURAL
              </span>
              <span
                style={{
                  background: "rgba(255, 94, 126, 0.15)",
                  color: "#FF5E7E",
                  border: "1px solid rgba(255, 94, 126, 0.35)",
                  padding: "6px 14px",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: "0.05em",
                }}
              >
                CHIBI PET VIBE
              </span>
            </div>
            <span
              style={{
                color: "#71717A",
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: "0.05em",
              }}
            >
              ZERO GLTF • PURE CODE
            </span>
          </div>

          {/* Speech Quote */}
          <div
            style={{
              color: "#FFFFFF",
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.4,
              borderLeft: "4px solid #00E5FF",
              paddingLeft: 14,
            }}
          >
            "Halo Zura! Kenalin aku Zui! Tubuhku 100% dibuat dari Three.js di Remotion. Ringan, imut, dan siap dianimasiin buat petualangan baru!"
          </div>

          {/* Specs Mini Bar */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 12,
              paddingTop: 8,
              borderTop: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            <div>
              <div style={{ color: "#71717A", fontSize: 13, fontWeight: 700 }}>RENDER STATUS</div>
              <div style={{ color: "#22C55E", fontSize: 16, fontWeight: 800 }}>LIVE AT 60 FPS</div>
            </div>
            <div>
              <div style={{ color: "#71717A", fontSize: 13, fontWeight: 700 }}>EXPRESSION</div>
              <div style={{ color: "#F4F4F5", fontSize: 16, fontWeight: 800 }}>WAVE & BLINK</div>
            </div>
            <div>
              <div style={{ color: "#71717A", fontSize: 13, fontWeight: 700 }}>LIGHTING</div>
              <div style={{ color: "#00E5FF", fontSize: 16, fontWeight: 800 }}>STUDIO 3-POINT</div>
            </div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
